/**
 * The pipeline a numbered process list describes.
 *
 * `process_order` maps a stage_key to the number typed against it, and that
 * numbering IS the pipeline — the server builds the same list from the same
 * numbers in `services/stage_engine.build_pipeline_from_picks`. These live here
 * rather than beside either picker because both the desktop one and the
 * supervisor's phone one resolve against them, and two copies of this could
 * disagree with each other and with the server.
 */

// appended to every pipeline, so they are never something to choose
const ANCHORS = new Set(['packing', 'dispatch']);

export function pickableStages(rules = []) {
  return [...rules]
    .filter(rule => !ANCHORS.has(rule.stage_key))
    .sort((a, b) => (a.default_sequence || 0) - (b.default_sequence || 0));
}

/**
 * The pipeline the numbers describe, so the preview matches what the server
 * will build from the same numbers (services/stage_engine.build_pipeline_from_picks).
 * A step that runs after the goods have left — signage installation — sits
 * behind Dispatch wherever its number put it.
 */
export function resolvePickedPipeline(processOrder, rules = []) {
  const byKey = Object.fromEntries(rules.map(rule => [rule.stage_key, rule]));
  const picked = Object.entries(processOrder)
    .filter(([key]) => byKey[key] && !ANCHORS.has(key))
    .sort((a, b) => a[1] - b[1])
    .map(([key]) => byKey[key]);
  if (picked.length === 0) return [];

  return [
    ...picked.filter(rule => !rule.after_dispatch),
    { stage_name: 'Packing', stage_key: 'packing' },
    { stage_name: 'Dispatch', stage_key: 'dispatch' },
    ...picked.filter(rule => rule.after_dispatch),
  ];
}

/**
 * The spec field that carries a stage's instruction, if it has one.
 *
 * A stage is picked by a single presence test — a filled field, or a Yes — and
 * that field is the instruction. Rules with no conditions (Design, Corrugation,
 * Packing) are asked for by number alone, and a second condition is a modifier
 * (`printing_method != Screen`) rather than the thing being picked, so only the
 * first is read.
 */
export function instructionFieldName(rule) {
  const [condition] = rule.conditions || [];
  if (!condition?.field) return null;
  if (condition.operator === 'not_empty') return condition.field;
  if (condition.operator === 'equals' && condition.value === 'Yes') return condition.field;
  return null;
}

/** Every field the process list owns, so the spec sheet does not show it too. */
export function instructionFieldNames(rules = []) {
  return new Set(pickableStages(rules).map(instructionFieldName).filter(Boolean));
}
